import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import { ChevronDown, ExternalLink } from "lucide-react";
import { socialLinks } from "../data/socialLinks";

export default function Header({ logo, logo_text, language, setLanguage, toggleLanguage, texts, onWeixinClick }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [openIndex, setOpenIndex] = useState(null);

    const toggleSubmenu = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const menuVariants = {
        hidden: { x: "100%", opacity: 0 },
        visible: { x: 0, opacity: 1 },
    };

    const youtube = socialLinks.find(s => s.label === "YouTube");
    const otherSocial = [
        ...socialLinks.filter(s => s.label !== "YouTube" && s.label !== "WeChat" && s.href),
        { label: "WeChat", action: onWeixinClick },
    ];

    const menuItems = [
        { label: texts?.header?.strokeOrder, href: "/strokeorder" },
        { label: texts?.header?.game,        href: "/game" },
        { label: texts?.header?.typing,      href: "/typing" },
        { label: texts?.header?.vocabulary,  href: "/vocabulary" },
        { label: texts?.header?.worksheets,  href: "/writing" },
        youtube,
        { label: language === "JP" ? "フォローしてね" : language === "ZH" ? "关注我" : "Follow Me", submenu: otherSocial },
        { label: texts?.header?.about,    href: "/about" },
        { label: texts?.header?.contact,  href: "/contact" },
        // { label: texts?.header?.message, href: "/bookalesson" }, // hidden for now
    ].filter(Boolean);

    if (!texts) return null;

    return (
        <header className="fixed top-0 left-0 w-full flex justify-between items-center p-4 z-[40]" style={{ transform: "translateZ(0)" }}>
            {/* ロゴ */}
            <Link to="/">
                <div className="flex items-center gap-0">
                    <img src={logo} alt="Logo" className="w-10 h-10" />
                    <img src={logo_text} alt="logo_text" className="h-10 w-25" />
                </div>
            </Link>

            {/* 言語切替 + ハンバーガー */}
            <div className="flex items-center gap-3 z-[60]">
                <LanguageSwitcher language={language} setLanguage={setLanguage || toggleLanguage} />

                {/* ハンバーガー（PCでも表示） */}
                <button onClick={() => setMenuOpen(!menuOpen)} className="focus:outline-none">
                    <motion.svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#b5435f"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        animate={menuOpen ? "open" : "closed"}
                        variants={{
                            open: { rotate: 90 },
                            closed: { rotate: 0 },
                        }}
                        transition={{ type: "spring", stiffness: 250, damping: 20 }}
                    >
                        {menuOpen ? (
                            <motion.path d="M6 6L18 18M6 18L18 6" />
                        ) : (
                            <motion.path d="M3 6h18M3 12h18M3 18h18" />
                        )}
                    </motion.svg>
                </button>
            </div>

            {/* モバイル＆PC共通メニュー */}
            <AnimatePresence>
                {menuOpen && (
                    <motion.nav
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={menuVariants}
                        transition={{ type: "spring", stiffness: 120, damping: 14 }}
                        className="fixed top-0 right-0 h-screen bg-white/95 backdrop-blur-sm shadow-2xl z-[50]
                        flex flex-col justify-center items-center gap-4 text-center
                        w-full md:w-1/3" // ← PC時は1/3幅に限定
                    >
                        {menuItems.map((item, i) =>
                            item.submenu ? (
                                <div key={i} className="w-full max-w-xs">
                                    <button
                                        onClick={() => toggleSubmenu(i)}
                                        className="flex justify-center items-center gap-2 text-xl font-semibold hover:text-[#b5435f] transition w-full"
                                    >
                                        {item.label}
                                        <ChevronDown
                                            className={`w-5 h-5 transition-transform ${openIndex === i ? "rotate-180" : ""}`}
                                        />
                                    </button>

                                    <AnimatePresence>
                                        {openIndex === i && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: "auto" }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.3 }}
                                                className="flex flex-col mt-2"
                                            >
                                                {item.submenu.map((sub, j) => (
                                                    sub.action ? (
                                                        <button key={j}
                                                            onClick={() => { sub.action(); setMenuOpen(false); }}
                                                            className="text-lg text-gray-700 py-1 hover:text-[#b5435f] transition">
                                                            {sub.label}
                                                        </button>
                                                    ) : (
                                                        <a key={j} href={sub.href}
                                                            className="text-lg text-gray-700 py-1 hover:text-[#b5435f] transition">
                                                            {sub.label}
                                                        </a>
                                                    )
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ) : item.action ? (
                                <button
                                    key={i}
                                    onClick={() => { item.action(); setMenuOpen(false); }}
                                    className="text-xl font-semibold hover:text-[#b5435f] transition flex gap-2 items-center"
                                >
                                    {item.label}
                                </button>
                            ) : item.href?.startsWith("/") ? (
                                <Link
                                    key={i}
                                    to={item.href}
                                    className="text-xl font-semibold hover:text-[#b5435f] transition flex gap-2 items-center"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {item.label}
                                </Link>
                            ) : (
                                <a
                                    key={i}
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xl font-semibold hover:text-[#b5435f] transition flex gap-2 items-center"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {item.label}
                                    <ExternalLink className="w-5 h-5" />
                                </a>
                            )
                        )}
                    </motion.nav>
                )}
            </AnimatePresence>

            {menuOpen && (
                <div
                    className="fixed inset-0 z-[45]"
                    onClick={() => setMenuOpen(false)}
                ></div>
            )}
        </header>
    );
}
